import { useClient, useMutation, useQuery } from '@weenat/client'
import {
  extractErrorsOnPhoneNumbersUpdate,
  useIsMemberOfExpertOrg
} from '@weenat/client/dist/core/me'
import { PhoneNumber, PhoneType } from '@weenat/client/dist/resources/me'
import { useIntl } from '@weenat/wintl'
import useDisclosure from 'app/src/hooks/useDisclosure'
import useToasts from 'app/src/hooks/useToasts'
import Button from 'app/src/kit/Button'
import ListEmpty from 'app/src/kit/ListEmpty'
import Modal from 'app/src/kit/Modal'
import Text from 'app/src/kit/Text'
import LoadingCircle from 'app/src/kit/loaders/LoadingCircle'
import { useUserContext } from 'app/userProvider'
import { isNil } from 'lodash-es'
import isArray from 'lodash-es/isArray'
import isEmpty from 'lodash-es/isEmpty'
import isString from 'lodash-es/isString'
import { transparentize } from 'polished'
import { Fragment, useMemo, useState } from 'react'
import { styled } from 'styled-components'
import useLogAndNavToUpgradePlans from '../hooks/useLogAndNavToUpgradePlans'
import CTACard from '../kit/CTACard'
import DelimitedFlex from '../kit/DelimitedFlex'
import Icons from '../kit/Icons'
import AccountSection from './AccountSection'
import AddPhoneForm, { SinglePhoneFormValues } from './AddPhoneForm'
import SecondaryPhoneNumberItem from './SecondaryPhoneNumberItem'

const MAX_NUMBER_OF_PHONES = 5

const EmptyPrimaryPhoneContainer = styled(Flex)`
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  border-radius: ${(p) => p.theme.radiuses.md}px;
  margin-top: ${(p) => p.theme.spacings.lg}px;
  padding: ${(p) => p.theme.spacings.lg}px;
  border: 1px solid ${(p) => p.theme.colors.grayscale[100]};
  background-color: ${(props) => transparentize(0.5, props.theme.colors.grayscale[100])};
`

function sortByPrimaryAttribute(phones: PhoneNumber[]) {
  return phones.sort((phoneA, phoneB) => (phoneA.isPrimary && !phoneB.isPrimary ? -1 : 1))
}

const EMPTY_PHONE_NUMBERS: PhoneNumber[] = []

interface EditMyPhoneNumbersProps {
  insideModal?: boolean
  onCancel?: () => void
}

const EditMyPhoneNumbers: FC<EditMyPhoneNumbersProps> = ({ insideModal = false, onCancel }) => {
  const { t } = useIntl()
  const { addSuccessToast, addErrorToast } = useToasts()
  const { me } = useUserContext()

  const {
    isOpen: isSecondaryPhonesModalOpen,
    close: closeSecondaryPhonesModal,
    open: openSecondaryPhonesModal
  } = useDisclosure()
  const client = useClient()

  const { isMemberOfExpertOrg, myInfoRequest } = useIsMemberOfExpertOrg({ isEnabled: true })

  const allPhoneNumbersRequest = useQuery(client.me.phoneNumbers.getAll())
  const allPhones = allPhoneNumbersRequest.data ?? EMPTY_PHONE_NUMBERS

  // Tracks unsaved field edits (countryCode / number) per phone ID
  const [phoneEdits, setPhoneEdits] = useState<Record<number, Partial<PhoneNumber>>>({})
  // Array of error message sorted in the same order as sortedPhones
  const [phoneFieldsErrorMessages, setPhoneFieldsErrorMessages] = useState<(string | undefined)[]>(
    []
  )

  const [typeOfPhoneInCreation, setTypeOfPhoneInCreation] = useState<PhoneType>('none')

  // Derived: merge server data with local edits, sorted primary-first
  const sortedPhones = useMemo(
    () => sortByPrimaryAttribute([...allPhones].map((p) => ({ ...p, ...phoneEdits[p.id] }))),
    [allPhones, phoneEdits]
  )

  const [updatePhones, updatePhonesRequest] = useMutation(client.me.phoneNumbers.updateAll(), {
    onSuccess: () => {
      setTypeOfPhoneInCreation('none')
      setPhoneFieldsErrorMessages([])
      setPhoneEdits({})

      addSuccessToast(t('user_settings.phone_numbers.all_numbers_edition_success'))
    },
    onError: (e) => {
      const err = extractErrorsOnPhoneNumbersUpdate(e)

      if (isString(err)) {
        addErrorToast(err)
      } else if (isArray(err)) {
        setPhoneFieldsErrorMessages(err)
      }
    }
  })

  const sortAndSendUpdate = (newPhones: PhoneNumber[]) => {
    const sorted = sortByPrimaryAttribute(newPhones)
    return updatePhones(sorted)
  }

  const handleMultiplePhonesSubmit = () => {
    return sortAndSendUpdate(sortedPhones)
  }

  const handleCountryChange = (phoneId: number, newCountryCode: PhoneNumber['countryCode']) => {
    setPhoneEdits((prev) => ({
      ...prev,
      [phoneId]: { ...prev[phoneId], countryCode: newCountryCode }
    }))
  }

  const handleNumberChange = (phoneId: number, newNumber: PhoneNumber['number']) => {
    setPhoneEdits((prev) => ({ ...prev, [phoneId]: { ...prev[phoneId], number: newNumber } }))
  }

  const handleAddPhoneFormSubmit = (newPhone: SinglePhoneFormValues) => {
    const toAdd: Omit<PhoneNumber, 'id'> = {
      countryCode: newPhone.telephoneCountryCode,
      isEnabled: newPhone.enabled,
      isPrimary: typeOfPhoneInCreation === 'primary',
      number: newPhone.telephoneNumber,
      userId: me.id
    }

    // not using sortedPhones to avoid error from ongoing edition form
    const newPhones = [...allPhones, toAdd]
    sortAndSendUpdate(newPhones as PhoneNumber[])
  }

  const isLoading = allPhoneNumbersRequest.isLoading || myInfoRequest.isLoading
  const isAddFormVisible = typeOfPhoneInCreation !== 'none'

  const Container = insideModal ? Fragment : AccountSection
  const containerProps = insideModal
    ? {}
    : { title: '', description: t('user_settings.phone_numbers.phones_usage_description') }

  const goToPlan = useLogAndNavToUpgradePlans()

  const resetPhoneInEdition = () => setTypeOfPhoneInCreation('none')

  return (
    <>
      <Box>
        <Container {...containerProps}>
          <Box $my={insideModal ? undefined : 24} $p={insideModal ? 'lg' : undefined}>
            {insideModal ? null : (
              <Text $fontWeight='bold' $lineHeight='md'>
                {t('user_settings.phone_numbers.section_label', { capitalize: true })}
              </Text>
            )}

            {isLoading ? (
              <LoadingCircle />
            ) : isEmpty(sortedPhones) ? (
              isAddFormVisible ? (
                <AddPhoneForm
                  phoneType='primary'
                  onSubmit={handleAddPhoneFormSubmit}
                  onCancel={resetPhoneInEdition}
                  buttonState={updatePhonesRequest}
                />
              ) : (
                <EmptyPrimaryPhoneContainer>
                  <Text $textAlign='center'>
                    {t('user_settings.phone_numbers.no_primary_phone', { capitalize: true })}
                  </Text>
                  <Box $mt='lg'>
                    <Button
                      IconLeft={Icons.PlusSignCircle}
                      onPress={() => setTypeOfPhoneInCreation('primary')}
                    >
                      {t('user_settings.phone_numbers.add_primary_phone')}
                    </Button>
                  </Box>
                </EmptyPrimaryPhoneContainer>
              )
            ) : (
              <>
                {sortedPhones.map((phone, index) => {
                  return index === 0 ? (
                    <Box key={`${phone.id}-primary`}>
                      <SecondaryPhoneNumberItem
                        disableDeletion
                        disableActivation
                        label={t('user_settings.phone_numbers.primary_phone', { capitalize: true })}
                        phone={phone}
                        position={index}
                        onCountryChange={handleCountryChange}
                        onNumberChange={handleNumberChange}
                        error={phoneFieldsErrorMessages[index]}
                      />
                      {isMemberOfExpertOrg ? (
                        <Box>
                          <Flex $alignItems='center' $justifyContent='space-between' $mb='lg'>
                            <Flex $alignItems='center' $flex={1} $gap={'md'}>
                              <Text $fontWeight='bold'>
                                {t('user_settings.phone_numbers.secondary_phones', {
                                  capitalize: true
                                })}
                              </Text>
                              {!insideModal ? (
                                <Icons.InfoCircle
                                  $ml='sm'
                                  $mr='md'
                                  $size='md'
                                  onPress={openSecondaryPhonesModal}
                                />
                              ) : null}
                            </Flex>
                          </Flex>
                          {sortedPhones.length === 1 ? (
                            <ListEmpty
                              model='phoneNumber'
                              message={t('user_settings.phone_numbers.no_secondary_phones_entered')}
                            />
                          ) : null}
                        </Box>
                      ) : null}
                    </Box>
                  ) : isMemberOfExpertOrg ? (
                    <SecondaryPhoneNumberItem
                      // eslint-disable-next-line react/no-array-index-key
                      key={`${phone.id}-${index}`}
                      phone={phone}
                      position={index}
                      onCountryChange={handleCountryChange}
                      onNumberChange={handleNumberChange}
                      error={phoneFieldsErrorMessages[index]}
                    />
                  ) : null
                })}
                {isAddFormVisible ? (
                  <AddPhoneForm
                    phoneType={typeOfPhoneInCreation}
                    onSubmit={handleAddPhoneFormSubmit}
                    onCancel={resetPhoneInEdition}
                    buttonState={updatePhonesRequest}
                  />
                ) : null}
                {isMemberOfExpertOrg && !isAddFormVisible ? (
                  <Flex $justifyContent='center'>
                    <Button
                      IconLeft={Icons.PlusSignCircle}
                      variant='compact'
                      color='grayscale.white'
                      backgroundColor='grayscale.black'
                      onPress={() => setTypeOfPhoneInCreation('secondary')}
                      isDisabled={
                        !isNil(sortedPhones) && sortedPhones.length >= MAX_NUMBER_OF_PHONES
                      }
                    >
                      {t('user_settings.phone_numbers.add_secondary_phone', {
                        capitalize: true
                      })}
                    </Button>
                  </Flex>
                ) : !isMemberOfExpertOrg ? (
                  <Flex $justifyContent='center'>
                    <CTACard
                      title={t('user_settings.phone_numbers.CTA_title')}
                      cta={t('ecommerce.plans.expert.cta_short')}
                      description={t('user_settings.phone_numbers.CTA_description')}
                      onCTAPress={goToPlan}
                      maxWidth={400}
                    />
                  </Flex>
                ) : null}
              </>
            )}
          </Box>

          {isEmpty(sortedPhones) ? null : !insideModal ? (
            <Flex $width='100%' $alignItems='center' $justifyContent='flex-end'>
              <Button
                onPress={() => {
                  handleMultiplePhonesSubmit()
                }}
                state={updatePhonesRequest}
              >
                {t('actions.save_changes')}
              </Button>
            </Flex>
          ) : (
            <DelimitedFlex $isDelimitedOnTop $p='lg' $gap='md' $justifyContent='flex-end'>
              <Button variant='compact' importance='sd' onPress={onCancel}>
                {t('actions.cancel')}
              </Button>
              <Button
                variant='compact'
                onPress={async () => {
                  await handleMultiplePhonesSubmit()
                  onCancel?.()
                }}
                state={updatePhonesRequest}
              >
                {t('actions.confirm')}
              </Button>
            </DelimitedFlex>
          )}
        </Container>
      </Box>

      <Modal
        isOpen={isSecondaryPhonesModalOpen}
        close={closeSecondaryPhonesModal}
        width={600}
        title={t('user_settings.phone_numbers.add_secondary_phone')}
      >
        <Text>
          {t('user_settings.phone_numbers.secondary_phones_explanation', {
            capitalize: true
          })}
        </Text>
      </Modal>
    </>
  )
}

export default EditMyPhoneNumbers
